import { Fragment, ReactNode } from 'react'
import { FiltersType, PersonType } from 'models'
import { IframeContainer } from 'components/article-content'
import { YouTube } from 'styles/misc'

const sharePrefixes = (url: string, title: string) => ({
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
  twitter: `https://twitter.com/share?text=${title}&url=${url}`,
  vk: `http://vk.com/share.php?url=${url}`,
  email: `mailto:?body=${url}&subject=${title}`,
  telegram: `https://t.me/share/url?url=${url}`,
  odnoklassniki: `http://www.odnoklassniki.ru/dk?st.cmd=addShare&st._surl=${url}`,
  whatsapp: `https://web.whatsapp.com/send?text=${url}`,
})

export type ServiceEnum = keyof ReturnType<typeof sharePrefixes>

export const buildShareUrl = (service: ServiceEnum) => {
  const url = encodeURIComponent(window.location.href)
  const title = encodeURIComponent('ҚАНДЫ ҚАҢТАР ҚҰРБАНДАРЫ')
  return sharePrefixes(url, title)[service]
}

const processInline = (block: string) => {
  let parts: ReactNode[] = []

  const linkPattern = /(\[[^|]+\|[^\]]+\])/gu
  const linkPartsPattern = /\[([^|]+)\|([^\]]+)\]/u

  const boldPattern = /(\*\*[^*]+\*\*)/gu
  const boldPartsPattern = /(\*\*([^*]+)\*\*)/u

  const italicPattern = /(__[^_]+__)/gu
  const italicPartsPattern = /(__([^_]+)__)/u

  parts = block.split(linkPattern)
  parts = parts.map((p) => (p as string).split(boldPattern)).flat()
  parts = parts.map((p) => (p as string).split(italicPattern)).flat()

  parts = parts.map((part) => {
    if (linkPartsPattern.test(part as string)) {
      const tokens = (part as string).match(linkPartsPattern) as [
        string,
        string,
        string
      ]
      return (
        <a href={tokens[2]} target="_blank" rel="noreferrer">
          {tokens[1]}
        </a>
      )
    } else if (boldPartsPattern.test(part as string)) {
      const tokens = (part as string).match(boldPartsPattern) as [
        string,
        string,
        string
      ]
      return <strong>{tokens[2]}</strong>
    } else if (italicPartsPattern.test(part as string)) {
      const tokens = (part as string).match(italicPartsPattern) as [
        string,
        string,
        string
      ]
      return <em>{tokens[2]}</em>
    }

    return part
  })

  return parts.length ? (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>{part}</Fragment>
      ))}
    </>
  ) : (
    block
  )
}

// Removes GDB url params
const cleanImageUrl = (url: string) => {
  const matches = url.match(
    /(https:\/\/gdb\.rferl\.org\/[a-z0-9-]+)[_a-z0-9]*(\.[a-zA-Z]+)/u
  )
  return Array.isArray(matches) && matches.length === 3
    ? `${matches[1]}${matches[2]}`
    : url
}

const processBlock = (part: string) => {
  const cleanPart = part.trim()

  if (/^image:([^:]+):https?:/u.test(cleanPart)) {
    const [_, description, ...rawImgUrl] = cleanPart.split(':')
    return (
      <figure>
        <img src={cleanImageUrl(rawImgUrl.join(':'))} alt="" />
        <figcaption>{description}</figcaption>
      </figure>
    )
  }

  if (/^image:/u.test(cleanPart)) {
    // Removes GDB url params
    const rawImgUrl = cleanPart.replace('image:', '')
    return <img src={cleanImageUrl(rawImgUrl)} alt="" />
  }

  if (/^video:/u.test(cleanPart)) {
    return (
      <video width="100%" controls>
        <source src={cleanPart.replace('video:', '')} />
      </video>
    )
  }

  if (/^iframe:/u.test(cleanPart)) {
    return (
      <IframeContainer>
        {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
        <iframe
          src={cleanPart.replace('iframe:', '')}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </IframeContainer>
    )
  }

  if (/^youtube:/u.test(cleanPart)) {
    return (
      <YouTube
        src={`https://www.youtube.com/embed/${cleanPart.replace(
          /^youtube:( )?/u,
          ''
        )}`}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      ></YouTube>
    )
  }

  if (/^##/u.test(cleanPart)) {
    return <h3>{cleanPart.replace(/^##/u, '')}</h3>
  }

  if (/^\*\*.*\*\*/u.test(cleanPart)) {
    const matches = cleanPart.match(/^\*\*(.*)\*\*/u)

    if (matches && matches[1]) {
      return <h3>{processInline(matches[1])}</h3>
    } else {
      return <h3>{processInline(cleanPart)}</h3>
    }
  }

  return <p>{processInline(cleanPart)}</p>
}

export const parseMarkdown = (text: string) => {
  const parts = text.split(/\n+/)

  return parts
    .filter((p) => p.trim())
    .map((part, index) => <Fragment key={index}>{processBlock(part)}</Fragment>)
}
export const imageWidth = (url: string, width: number) => {
  const newUrl =
    url.split('.')[0] +
    '.' +
    url.split('.')[1] +
    '.' +
    url.split('.')[2] +
    '_w' +
    width +
    '.' +
    url.split('.')[3]
  return newUrl
}
export const sanitizeImageUrl = (url: string | undefined, width: number) => {
  if (!url) {
    return url
  }

  const matchReg = /(https:\/\/gdb\.rferl\.org\/[a-z0-9-]+).*(\.jpe?g)/u

  if (matchReg.test(url)) {
    const matches = url.match(matchReg)

    if (matches?.length === 3) {
      return `${matches[1]}_w${width}${matches[2]}`
    }
  }

  return url || ''
}

export const buildPeopleObjects = (data: { values: Array<string[]> }) => {
  const dataCopy = data.values.slice()
  const columnNames = dataCopy.shift() as string[]

  // Remove column names in KZ
  dataCopy.shift()

  const people = dataCopy.map((row, rowIndex) =>
    row.reduce(
      (acc, value, index) => {
        const columnName = columnNames[index] as keyof FiltersType

        return {
          ...acc,
          [columnName]: value,
        }
      },
      { id: rowIndex + 1 } as PersonType
    )
  )

  return people
}
